import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useTheme, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Badge, Box, IconButton } from '@mui/material';
import {
  SupervisorAccountOutlined,
  LogoutOutlined,
  ArticleOutlined,
  SignalCellularAltOutlined,
  PostAddOutlined,
  ManageAccountsOutlined,
  StorageOutlined,
  BookmarkBorderOutlined,
  NewspaperOutlined,
  DescriptionOutlined,
  QuestionAnswerOutlined,
  SettingsOutlined,
  FactCheckOutlined,
  ReceiptLongOutlined,
  PersonOutlined,
  DashboardOutlined,
  AssessmentOutlined,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

import { FormLogo } from '../assets/icons';
import { AuthenticationService } from '../services';
import { SideNavbarContext, UnreadContext, UserContext } from '../contexts';

const NavBar = ({ logo, styles, open, handleDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [translate] = useTranslation();

  const { selected, setSelected } = useContext(SideNavbarContext);
  const { messagesCount } = useContext(UnreadContext);
  const { currentUser, setCurrentUser, isSuperAdmin, setIsSuperAdmin } = useContext(UserContext);

  const {
    approvedDomains,
    blogs,
    dashboard,
    featuredContent,
    inventoryManagement,
    invoice,
    messagingCenter,
    news,
    ordersManagement,
    pages,
    proposalRequest,
    reports,
    successStories,
    systemSettings,
    user,
    usersApproval,
  } = currentUser?.permissions?.administration ?? {};

  const onLogout = () => {
    AuthenticationService.logout();
    setSelected('');
    setCurrentUser(undefined);
    setIsSuperAdmin(false);
    navigate('/auth');
  };

  const handleDrawer = openDrawer => handleDrawerToggle(openDrawer);

  const navBarOptions = [
    ...(isSuperAdmin || dashboard?.view
      ? [
          {
            id: 1,
            text: 'dashboard',
            icon: <DashboardOutlined />,
            state: 'dashboard',
            onclick: () => {
              navigate('/dashboard');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || reports?.view
      ? [
          {
            id: 2,
            text: 'reports',
            icon: <AssessmentOutlined />,
            state: 'reports',
            onclick: () => {
              navigate('/reports');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || user?.view
      ? [
          {
            id: 3,
            text: 'userManagement',
            icon: <SupervisorAccountOutlined />,
            state: 'userManagement',
            onclick: () => {
              navigate('/user');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || blogs?.view
      ? [
          {
            id: 4,
            text: 'blogs',
            icon: <ArticleOutlined />,
            state: 'blogs',
            onclick: () => {
              navigate('/cms/blog');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || pages?.view
      ? [
          {
            id: 5,
            text: translate('cmsPagesBreadcrumbs'),
            icon: <PostAddOutlined />,
            state: 'pages',
            onclick: () => {
              navigate('/cms');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || successStories?.view
      ? [
          {
            id: 6,
            text: 'successStories',
            icon: <ArticleOutlined />,
            state: 'successStories',
            onclick: () => {
              navigate('/cms/success-stories');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || approvedDomains?.view
      ? [
          {
            id: 7,
            text: 'approvedDomain',
            icon: <SignalCellularAltOutlined />,
            state: 'approvedDomains',
            onclick: () => {
              navigate('/approved-domains');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || usersApproval?.view
      ? [
          {
            id: 8,
            text: 'usersApproval',
            icon: <ManageAccountsOutlined />,
            state: 'usersApproval',
            onclick: () => {
              navigate('/users-approval');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || proposalRequest?.view
      ? [
          {
            id: 9,
            text: 'proposalRequests',
            icon: <DescriptionOutlined />,
            state: 'proposalRequests',
            onclick: () => {
              navigate('/proposal-requests');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || inventoryManagement?.view
      ? [
          {
            id: 10,
            text: translate('inventoryManagementNavBar'),
            icon: <StorageOutlined />,
            state: 'inventoryManagement',
            onclick: () => {
              navigate('/inventory-management');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || featuredContent?.view
      ? [
          {
            id: 11,
            text: translate('featuredContentPageTitle'),
            icon: <BookmarkBorderOutlined />,
            state: 'featuredContent',
            onclick: () => {
              navigate('/featured-content');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || news?.view
      ? [
          {
            id: 12,
            text: 'news',
            icon: <NewspaperOutlined />,
            state: 'news',
            onclick: () => {
              navigate('/cms/news');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || invoice?.view
      ? [
          {
            id: 13,
            text: 'invoicesPageTitle',
            icon: <ReceiptLongOutlined />,
            state: 'invoicesPageTitle',
            onclick: () => {
              navigate('/invoice');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || messagingCenter?.view
      ? [
          {
            id: 14,
            text: 'messageCenter',
            icon: (
              <Badge color='info' badgeContent={messagesCount} max={9}>
                <QuestionAnswerOutlined />
              </Badge>
            ),
            state: 'messageCenter',
            onclick: () => {
              navigate('/message');
            },
          },
        ]
      : []),
    ...(isSuperAdmin || ordersManagement?.view
      ? [
          {
            id: 13,
            text: 'ordersManagement',
            icon: <FactCheckOutlined />,
            state: 'ordersManagement',
            onclick: () => {
              navigate('/orders');
            },
          },
        ]
      : []),
    {
      id: 14,
      text: 'manageProfile',
      icon: <PersonOutlined />,
      state: 'manageProfile',
      onclick: () => {
        navigate('/user/manage-profile');
      },
    },
    ...(isSuperAdmin || systemSettings?.view
      ? [
          {
            id: 15,
            text: 'settings',
            icon: <SettingsOutlined />,
            state: 'settings',
            onclick: () => {
              navigate('/settings/system');
            },
          },
        ]
      : []),
  ];

  return (
    <Grid
      item
      container
      flexDirection='column'
      sx={{
        flexWrap: 'nowrap',
        height: '100%',
        position: 'sticky',
        minWidth: '22rem',
        pt: theme.spacing(5),
        alignItems: 'center',
        gap: theme.spacing(2),
        backgroundColor: theme.palette.white.main,
        ...styles,
      }}>
      {logo ? (
        <Box sx={{ px: theme.spacing(3), display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box>
            <IconButton onClick={() => handleDrawer(!open)}>
              <CloseIcon sx={{ height: '2.1rem', width: '2.1rem' }} />
            </IconButton>
          </Box>
          <FormLogo />
        </Box>
      ) : (
        <></>
      )}
      <Grid item container direction='column' justifyContent='space-between' wrap='nowrap' height='100%' overflow='auto'>
        <List sx={{ justifyContent: 'space-between' }} component='nav'>
          {navBarOptions.map(({ id, icon, text, onclick, state }) => (
            <ListItem key={id}>
              <ListItemButton selected={state === selected} onClick={onclick}>
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={translate(text)} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <List>
          <ListItem>
            <ListItemButton onClick={onLogout}>
              <ListItemIcon>{<LogoutOutlined />}</ListItemIcon>
              <ListItemText primary={translate('logOut')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Grid>
    </Grid>
  );
};

export default NavBar;
